import { DateTime } from 'luxon';
import { AlertTriangle, EmailBlock, Inbox, Mail } from '@saleshandy/icons';
import durationOptions from '../../../../../shared/components/date-filter/date-filter-options';
import { constants } from '../../../../../shared/enums/constants';
import { InboxRadarSortBy, InboxRadarSortOrder } from './enums';
import { InboxRadarFilters } from '../types';
import { TestSummaryResponse } from '../../../types';
import { StorageUtil } from '../../../../../shared/utils/storage';

const INBOX_RADAR_DASHBOARD_FILTERS_LS_KEY = 'inboxRadarDashboardFilters';

export const getInboxRadarFiltersFromLocalStorage = () => {
  const filtersString = StorageUtil.getItem(
    INBOX_RADAR_DASHBOARD_FILTERS_LS_KEY,
  );
  if (filtersString) {
    try {
      const parsed = JSON.parse(filtersString);
      if (parsed.date && parsed.date.startDate && parsed.date.endDate) {
        parsed.date.startDate = DateTime.fromISO(
          parsed.date.startDate as string,
        );
        parsed.date.endDate = DateTime.fromISO(parsed.date.endDate as string);
      }
      return parsed;
    } catch (error) {
      console.error('Error parsing filters from localStorage:', error);
      StorageUtil.removeItem(INBOX_RADAR_DASHBOARD_FILTERS_LS_KEY);
      return null;
    }
  }
  return null;
};

export const getDefaultInboxRadarFilters = () => {
  const filtersFromLS = getInboxRadarFiltersFromLocalStorage();

  if (filtersFromLS) {
    let date = null;

    if (filtersFromLS.date) {
      const selectedDate = durationOptions.find(
        (item) => item.label === filtersFromLS.date.label,
      );

      if (filtersFromLS.date.label === 'custom') {
        const sDate = DateTime.fromJSDate(
          new Date(filtersFromLS.date.startDate),
        );
        const eDate = DateTime.fromJSDate(new Date(filtersFromLS.date.endDate));
        date = {
          label: selectedDate.label,
          startDate: sDate,
          endDate: eDate,
          displayText: selectedDate.displayText,
        };
      } else {
        date = selectedDate;
      }
    }

    return {
      date: date || durationOptions[4],
      page: filtersFromLS.page || 1,
      limit: filtersFromLS.limit || constants.DEFAULT_PAGE_SIZE,
      search: filtersFromLS.search || '',
      orderBy: filtersFromLS.orderBy || InboxRadarSortBy.LastRunOn,
      sortOrder: filtersFromLS.sortOrder || InboxRadarSortOrder.Desc,
    };
  }

  return {
    date: durationOptions[4],
    page: 1,
    limit: constants.DEFAULT_PAGE_SIZE,
    search: '',
    orderBy: InboxRadarSortBy.LastRunOn,
    sortOrder: InboxRadarSortOrder.Desc,
  };
};

// Function to save filters to localStorage
export const setInboxRadarFiltersToLocalStorage = (filters) => {
  try {
    // Convert DateTime objects to ISO strings before saving
    const storableFilters = {
      ...filters,
      search: '',
      date: filters.date
        ? {
            ...filters.date,
            startDate:
              filters.date.startDate instanceof DateTime
                ? filters.date.startDate.toISO()
                : filters.date.startDate,
            endDate:
              filters.date.endDate instanceof DateTime
                ? filters.date.endDate.toISO()
                : filters.date.endDate,
          }
        : null,
    };
    StorageUtil.setItem(
      INBOX_RADAR_DASHBOARD_FILTERS_LS_KEY,
      JSON.stringify(storableFilters),
    );
  } catch (error) {
    console.error('Error saving filters to localStorage:', error);
  }
};

export const TestStatusDisplayName = {
  draft: 'Draft',
  paused: 'Paused',
  active: 'In Progress',
  completed: 'Completed',
  stopped: 'Stopped',
};

export const TestTypeDisplayName = {
  automated: 'Recurring',
  'manual-internal': 'One Time',
  'manual-external': 'External',
};

export const TestRepeatUnitDisplayName = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

export const getFirstTestListData = () => [
  {
    id: 'example-id',
    seedTestId: 'example-seed-test-id',
    name: 'Your First Test',
    type: 'automated',
    status: 'draft',
    repeatUnit: 'daily',
    testProgress: {
      totalEmails: null,
      sentEmails: null,
      percentage: null,
    },
    inboxScore: {
      value: null,
    },
    primary: {
      value: null,
      percentage: null,
    },
    spam: {
      value: null,
      percentage: null,
    },
    others: {
      value: null,
      percentage: null,
    },
    notDelivered: {
      value: null,
      percentage: null,
    },
    lastRunOn: null,
    userName: '',
  },
];

/**
 *
 * @param f1 - The first filter object
 * @param f2 - The second filter object
 * @returns true if the two filter objects are equal, false otherwise
 */
export const areFiltersEqual = (
  f1: InboxRadarFilters,
  f2: InboxRadarFilters,
): boolean => {
  if (!f1 || !f2) return f1 === f2; // Should not happen if types are correct

  if (f1.search !== f2.search) return false;
  if (f1.page !== f2.page) return false;
  if (f1.limit !== f2.limit) return false;
  if (f1.orderBy !== f2.orderBy) return false;
  if (f1.sortOrder !== f2.sortOrder) return false;

  // Compare date object properties
  if (f1.date?.label !== f2.date?.label) return false;

  const d1Start = f1.date?.startDate;
  const d1End = f1.date?.endDate;
  const d2Start = f2.date?.startDate;
  const d2End = f2.date?.endDate;

  if (d1Start && d2Start) {
    if (!d1Start.equals(d2Start)) return false;
  } else if (d1Start || d2Start) {
    // One is defined, the other is not
    return false;
  }

  if (d1End && d2End) {
    if (!d1End.equals(d2End)) return false;
  } else if (d1End || d2End) {
    // One is defined, the other is not
    return false;
  }

  return true;
};

/**
 *
 * @param oldFilters - The old filters object
 * @param newFilters - The new filters object
 * @returns true if only pagination/sorting filters changed, false otherwise
 */
export const isPaginationSortingOnlyChange = (
  oldFilters: InboxRadarFilters,
  newFilters: InboxRadarFilters,
): boolean => {
  if (!oldFilters || !newFilters) return false;

  // Check if data filters (date, search) are the same
  const dataFiltersUnchanged =
    oldFilters.search === newFilters.search &&
    oldFilters.date?.label === newFilters.date?.label &&
    oldFilters.date?.startDate?.equals(newFilters.date?.startDate) &&
    oldFilters.date?.endDate?.equals(newFilters.date?.endDate);

  // Check if at least one pagination/sorting filter changed
  const paginationSortingChanged =
    oldFilters.page !== newFilters.page ||
    oldFilters.limit !== newFilters.limit ||
    oldFilters.orderBy !== newFilters.orderBy ||
    oldFilters.sortOrder !== newFilters.sortOrder;

  return dataFiltersUnchanged && paginationSortingChanged;
};

export const getDashboardSummaryCardItems = (
  testSummaryData: TestSummaryResponse,
) => [
  {
    key: 'inbox',
    label: 'Inbox',
    icon: Inbox,
    percentage:
      testSummaryData?.sentCount > 0
        ? testSummaryData?.inbox?.percentage
        : null,
  },
  {
    key: 'spam',
    label: 'Spam',
    icon: AlertTriangle,
    percentage:
      testSummaryData?.sentCount > 0 ? testSummaryData?.spam?.percentage : null,
  },
  {
    key: 'other',
    label: 'Other',
    icon: Mail,
    percentage:
      testSummaryData?.sentCount > 0
        ? testSummaryData?.others?.percentage
        : null,
  },
  {
    key: 'undelivered',
    label: 'Undelivered',
    icon: EmailBlock,
    percentage:
      testSummaryData?.sentCount > 0
        ? testSummaryData?.notDelivered?.percentage
        : null,
  },
];
